import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "../../css/Home/mainList.css";
import ListItem from "../Common-components/listItem";
import HorizontalPagedList from "../Common-components/horizontalPagedList";
import TitleBar from "../Common-components/titleBar";
import CruiseCompanyItem from "../Common-components/cruiseCompanyItem";
import { BASE_IMAGE_URL } from "../Common-components/constants";
import { apiGet } from "../../utils/api";
import { normalizeReviewImageUrl } from "../../utils/reviewImage";

const DEFAULT_HOME_CRUISE_TAB = {
  key: "best",
  label: "베스트셀러",
  type: "best",
  regionId: null,
  imageUrl: null,
};

const formatReviewDate = (raw) => {
  if (!raw) return "";
  const normalized = String(raw).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return String(raw).split(" ")[0] || String(raw);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}.${month}.${day}`;
};

const buildCruiseInfoText = (cruise = {}) => {
  const shipName = String(cruise?.name || "").trim();
  const dep = String(cruise?.departure_port_name || "").trim();
  const arr = String(cruise?.arrival_port_name || "").trim();
  const route = [dep, arr].filter(Boolean).join(" - ");

  if (shipName && route) return `${shipName} | ${route}`;
  if (route) return route;
  return shipName;
};

function buildHomeCruiseTabs(summaryPayload) {
  const tabs = [DEFAULT_HOME_CRUISE_TAB];
  const entries = summaryPayload && typeof summaryPayload === "object"
    ? Object.entries(summaryPayload)
    : [];

  entries.forEach(([key, tab]) => {
    const regionId = Number(tab?.id) || 0;
    const label = String(tab?.name || "").trim();
    if (regionId <= 0 || !label) {
      return;
    }

    tabs.push({
      key,
      label,
      type: "region",
      regionId,
      imageUrl: tab?.image_url ? BASE_IMAGE_URL + tab.image_url : null,
    });
  });

  return tabs;
}

// 홈 메인 크루즈 섹션: 베스트셀러 기본 로드 후, 지역 탭 클릭 시 해당 데이터만 지연 로드한다.
export default function MainList({
  cruiseLines: initialCruiseLines,
  homeReviews: initialHomeReviews,
}) {
  const navigate = useNavigate();
  const [cruiseTabs, setCruiseTabs] = useState([DEFAULT_HOME_CRUISE_TAB]);
  const [activeTab, setActiveTab] = useState(DEFAULT_HOME_CRUISE_TAB.key);
  const [sectionItemsByTab, setSectionItemsByTab] = useState({});
  const [sectionMetaByTab, setSectionMetaByTab] = useState({});
  const [isTabsLoading, setIsTabsLoading] = useState(true);
  const [isSectionLoading, setIsSectionLoading] = useState(true);
  const [sectionError, setSectionError] = useState("");
  const [cruiseLines, setCruiseLines] = useState([]);
  const [homeReviews, setHomeReviews] = useState([]);

  const sectionItemsByTabRef = useRef(sectionItemsByTab);
  const sectionRequestMapRef = useRef({});
  const prefetchStartedRef = useRef(false);

  useEffect(() => {
    sectionItemsByTabRef.current = sectionItemsByTab;
  }, [sectionItemsByTab]);

  useEffect(() => {
    let cancelled = false;

    const fetchCruiseTabs = async () => {
      try {
        const res = await apiGet("/navigation/get_region_tabs.php", { summary: 1 });
        if (cancelled) return;
        if (res?.success) {
          setCruiseTabs(buildHomeCruiseTabs(res.data));
        } else {
          setCruiseTabs([DEFAULT_HOME_CRUISE_TAB]);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("메인 지역 탭 로딩 실패:", error);
        setCruiseTabs([DEFAULT_HOME_CRUISE_TAB]);
      } finally {
        if (!cancelled) {
          setIsTabsLoading(false);
        }
      }
    };

    fetchCruiseTabs();

    return () => {
      cancelled = true;
    };
  }, []);

  const activeTabConfig = useMemo(() => (
    cruiseTabs.find((tab) => tab.key === activeTab) || DEFAULT_HOME_CRUISE_TAB
  ), [cruiseTabs, activeTab]);

  const fetchCruiseSection = useCallback((tabConfig) => {
    if (!tabConfig?.key) {
      return Promise.resolve(null);
    }

    if (Object.prototype.hasOwnProperty.call(sectionItemsByTabRef.current, tabConfig.key)) {
      return Promise.resolve(sectionItemsByTabRef.current[tabConfig.key]);
    }

    if (sectionRequestMapRef.current[tabConfig.key]) {
      return sectionRequestMapRef.current[tabConfig.key];
    }

    const params = tabConfig.type === "best"
      ? { section: "best" }
      : { section: "region", region_id: tabConfig.regionId };
    const request = apiGet("/main/get_main_cruise_section.php", params)
      .then((res) => {
        if (!res?.success) {
          throw new Error("메인 크루즈 섹션 응답 실패");
        }

        const nextMeta = res?.data || null;
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        setSectionItemsByTab((prev) => ({
          ...prev,
          [tabConfig.key]: items,
        }));
        setSectionMetaByTab((prev) => ({
          ...prev,
          [tabConfig.key]: nextMeta,
        }));

        return nextMeta;
      })
      .finally(() => {
        delete sectionRequestMapRef.current[tabConfig.key];
      });

    sectionRequestMapRef.current[tabConfig.key] = request;
    return request;
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (Object.prototype.hasOwnProperty.call(sectionItemsByTab, activeTabConfig.key)) {
      setSectionError("");
      setIsSectionLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setSectionError("");
    setIsSectionLoading(true);

    fetchCruiseSection(activeTabConfig)
      .catch((error) => {
        if (cancelled) return;
        console.error("메인 크루즈 섹션 로딩 실패:", error);
        setSectionError("크루즈 정보를 불러오지 못했습니다.");
      })
      .finally(() => {
        if (!cancelled) {
          setIsSectionLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeTabConfig, sectionItemsByTab, fetchCruiseSection]);

  useEffect(() => {
    const hasBestSection = Object.prototype.hasOwnProperty.call(
      sectionItemsByTab,
      DEFAULT_HOME_CRUISE_TAB.key
    );

    if (prefetchStartedRef.current || isTabsLoading || !hasBestSection) {
      return undefined;
    }

    const connection = typeof navigator !== "undefined"
      ? navigator.connection || navigator.mozConnection || navigator.webkitConnection
      : null;
    const saveDataEnabled = Boolean(connection?.saveData);
    const effectiveType = String(connection?.effectiveType || "").toLowerCase();
    const shouldSkipPrefetch = saveDataEnabled || effectiveType === "slow-2g" || effectiveType === "2g";

    prefetchStartedRef.current = true;

    if (shouldSkipPrefetch) {
      return undefined;
    }

    let cancelled = false;
    let idleCallbackId = null;
    let timeoutId = null;

    const startPrefetch = async () => {
      const pendingTabs = cruiseTabs.filter((tab) => (
        tab.key !== DEFAULT_HOME_CRUISE_TAB.key
        && !Object.prototype.hasOwnProperty.call(sectionItemsByTabRef.current, tab.key)
      ));

      if (pendingTabs.length === 0) {
        return;
      }

      let cursor = 0;
      const runWorker = async () => {
        while (!cancelled && cursor < pendingTabs.length) {
          const currentIndex = cursor;
          cursor += 1;
          const nextTab = pendingTabs[currentIndex];
          if (!nextTab) {
            return;
          }

          try {
            await fetchCruiseSection(nextTab);
          } catch (error) {
            if (!cancelled) {
              console.error("메인 크루즈 탭 prefetch 실패:", error);
            }
          }
        }
      };

      const workerCount = Math.min(2, pendingTabs.length);
      await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
    };

    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      idleCallbackId = window.requestIdleCallback(() => {
        startPrefetch();
      }, { timeout: 1200 });
    } else if (typeof window !== "undefined") {
      timeoutId = window.setTimeout(() => {
        startPrefetch();
      }, 500);
    }

    return () => {
      cancelled = true;

      if (typeof window !== "undefined" && idleCallbackId !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleCallbackId);
      }

      if (typeof window !== "undefined" && timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [cruiseTabs, isTabsLoading, sectionItemsByTab, fetchCruiseSection]);

  useEffect(() => {
    if (initialCruiseLines !== undefined) {
      setCruiseLines(Array.isArray(initialCruiseLines) ? initialCruiseLines : []);
      return;
    }

    const fetchCruiseLines = async () => {
      try {
        const res = await apiGet("/main/get_main_cruise_lines.php");
        if (res.result === "success") {
          setCruiseLines(res.data);
        }
      } catch (err) {
        console.error("선사 정보 가져오기 실패:", err);
      }
    };

    fetchCruiseLines();
  }, [initialCruiseLines]);

  useEffect(() => {
    if (initialHomeReviews !== undefined && initialHomeReviews !== null) {
      const nextReviews = Array.isArray(initialHomeReviews) ? initialHomeReviews.slice(0, 3) : [];
      setHomeReviews((prev) => {
        if (nextReviews.length === 0 && Array.isArray(prev) && prev.length > 0) {
          return prev;
        }
        return nextReviews;
      });
      return;
    }

    let mounted = true;
    const fetchHomeReviews = async () => {
      try {
        const res = await apiGet("/review/get_reviews.php", { page: 1, limit: 3 });
        if (!mounted) return;
        if (res?.success) {
          setHomeReviews(Array.isArray(res.data) ? res.data.slice(0, 3) : []);
        } else {
          setHomeReviews([]);
        }
      } catch (_error) {
        if (!mounted) return;
        setHomeReviews([]);
      }
    };

    fetchHomeReviews();
    return () => {
      mounted = false;
    };
  }, [initialHomeReviews]);

  const currentSectionItems = useMemo(() => (
    Array.isArray(sectionItemsByTab[activeTab]) ? sectionItemsByTab[activeTab] : []
  ), [sectionItemsByTab, activeTab]);

  const currentSectionMeta = sectionMetaByTab[activeTab] || null;

  const currentSectionTitle = useMemo(() => {
    if (currentSectionMeta?.title) {
      return currentSectionMeta.title;
    }
    if (activeTabConfig.type === "best") {
      return "베스트셀러 크루즈";
    }
    return `${activeTabConfig.label} 크루즈`;
  }, [currentSectionMeta, activeTabConfig]);

  const transformItem = (item) => ({
    id: item.id,
    images: Array.isArray(item.images) ? item.images : [],
    departure: item.departure_port_name,
    destination: item.arrival_port_name,
    startDate: item.saildate,
    duration: item.nights ? `${item.nights}박 ${Number(item.nights) + 1}일` : "",
    ship: item.ship_name,
    ton: item.tonnage_label || "",
    price: item.price,
  });

  const shipNameItemData = cruiseLines.map((line) => ({
    id: line.id,
    icon: line.image_url ? BASE_IMAGE_URL + line.image_url : "",
    fullIcon: line.logourl ? line.logourl : "",
    name: line.korean_name || line.name || "",
    img: line.popup_image_url ? BASE_IMAGE_URL + line.popup_image_url : "",
    title: line.popup_title || "",
    desc: line.popup_description || "",
  }));

  const handleSeeAll = useCallback(() => {
    if (activeTabConfig.type === "best") {
      navigate("/bestlist");
      return;
    }

    if (activeTabConfig.regionId) {
      navigate(`/tabList?type=region&tabId=${activeTabConfig.key}`);
    }
  }, [activeTabConfig, navigate]);

  const renderSectionContent = () => {
    if (isSectionLoading) {
      return <div className="main-section-status">크루즈 정보를 불러오는 중입니다.</div>;
    }

    if (sectionError) {
      return <div className="main-section-status error">{sectionError}</div>;
    }

    if (currentSectionItems.length === 0) {
      return <div className="main-section-status">표시할 크루즈가 없습니다.</div>;
    }

    return (
      <HorizontalPagedList
        data={currentSectionItems}
        renderItem={(item, index) => (
          <div key={index} className="main-item">
            <ListItem item={transformItem(item)} />
          </div>
        )}
      />
    );
  };

  return (
    <div className="main-list-section-wrapper">
      <section className="main-section">
        <div className="swipe-tabs">
          <Swiper
            modules={[FreeMode]}
            slidesPerView="auto"
            freeMode
            grabCursor
            centeredSlides={false}
            slideToClickedSlide={false}
            spaceBetween={32}
            breakpoints={{
              651: { spaceBetween: 40 },
            }}
            className="main-swiper"
          >
            {cruiseTabs.map((tab) => (
              <SwiperSlide key={tab.key} className="main-slide">
                <button
                  type="button"
                  className={`main-button ${activeTab === tab.key ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                  disabled={isTabsLoading && tab.key !== DEFAULT_HOME_CRUISE_TAB.key}
                >
                  {tab.imageUrl && (
                    <img src={tab.imageUrl} alt={tab.label} />
                  )}
                  {tab.label}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      <section className="list-section">
        <div className="list-section-item">
          <TitleBar title={currentSectionTitle} onSeeAll={handleSeeAll} />
          <div className={`main-content${currentSectionItems.length === 0 ? " is-empty" : ""}`}>
            {renderSectionContent()}
          </div>
        </div>

        <div className="list-section-item">
          <TitleBar title="선사별 크루즈 일정 보기" />
          <div className="curise-schedule-wrapper">
            <CruiseCompanyItem item={shipNameItemData} />
          </div>
        </div>

        {homeReviews.length > 0 && (
          <div className="list-section-item home-review-section-item">
            <TitleBar title="와우크루즈 고객 후기" onSeeAll={() => navigate("/review")} />
            <div className="home-review-grid">
              {homeReviews.slice(0, 3).map((review) => {
                const reviewId = review?.id;
                const thumb = normalizeReviewImageUrl(review?.thumbnail_url || "");
                const cruiseInfoText = buildCruiseInfoText(review?.cruise || {});
                return (
                  <button
                    key={reviewId}
                    type="button"
                    className="home-review-card"
                    onClick={() => navigate(`/review/${reviewId}`)}
                  >
                    <div className="home-review-thumb">
                      {thumb ? (
                        <img src={thumb} alt={review?.title || "리뷰 이미지"} />
                      ) : (
                        <div className="home-review-placeholder">NO IMAGE</div>
                      )}
                      <div className="home-review-overlay">
                        <div className="home-review-title">{review?.title || ""}</div>
                        {cruiseInfoText && <div className="home-review-cruise">{cruiseInfoText}</div>}
                        <div className="home-review-link">자세히보기 {'>'}</div>
                        <div className="home-review-meta">
                          <span>{review?.author_name || "익명"}</span>
                          <span>{formatReviewDate(review?.published_at)}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
